import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import Loader from '../../modules/layout/components/Loader';
import AppBreadcrumb from '../../modules/layout/components/AppBreadcrumb';
import FiltersCard from '../../modules/layout/components/FiltersCard';
import Pagination from '../../modules/layout/components/Pagination';
import { useToast } from '../../modules/layout/components/ToastContext';
import { post } from '../_request';
import { Field, Form, Formik } from 'formik';
import SortDirection from '../../modules/layout/components/SortDirection';
import { FormikDatePicker } from '../../modules/layout/components/FormikDatePicker';
import FormikSelect from '../../modules/layout/components/Select/FormikSelect';
import { IAuditActionType, IFilterProfileEventsModel } from '../../interfaces/IUtilities';
import { AuditActionTypeEnumText } from '../../helpers/EnumeratorHelper';
import BackButton from '../../modules/layout/components/BackButton';
import { formatDateTime, parseUtc, toApiDateTime } from '../../helpers/DateTimeHelper';

type EventItem = {
  type: string;
  time: string;
  clientId: string;
  userId: string;
  ipAddress: string;
  error: string | null;
  userChanges: Record<string, { oldValue?: string; newValue?: string }> | null;
  roleChanges: { added?: string[]; removed?: string[] } | null;
  actorUserName?: string;
  actorFullName?: string;
  reason?: string;
  rawChanges?: string | null;
};




export default function ProfileEventsList() {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  const [events, setEvents] = useState<EventItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [ready, setReady] = useState(false);

  const [prefilledUserName, setPrefilledUserName] = useState(false);
  const [prefilledFullName, setPrefilledFullName] = useState(false);

  const [activeFilters, setActiveFilters] = useState<IFilterProfileEventsModel>({
    userName: '',
    fullName: '',
    userId: '',
    profileId: '',
    actionTypes: [],
    dateFrom: null,
    dateTo: null
  });
  const [asc, setAsc] = useState(true);
  const [sortField, setSortField] = useState<string>('timestamp');

  const nameRef = useRef<HTMLSpanElement | null>(null);

  const specialReasonLabels: Record<string, { typeKey: string; messageKey: string }> = {
    UPDATE_RESET_PASSWORD: { typeKey: 'PasswordResetType', messageKey: 'PasswordResetMessage' },
    UPDATE_ASSIGN_OTP: { typeKey: 'OtpAssignedType', messageKey: 'OtpAssignedMessage' },
    DELETE_RESET_OTP: { typeKey: 'OtpResetType', messageKey: 'OtpResetMessage' },
  };

  const actionTypesSelectOptions = useMemo(() => [
    { value: IAuditActionType.Action as number | string, label: AuditActionTypeEnumText[IAuditActionType.Action] },
    { value: IAuditActionType.Create as number | string, label: AuditActionTypeEnumText[IAuditActionType.Create] },
    { value: IAuditActionType.Update as number | string, label: AuditActionTypeEnumText[IAuditActionType.Update] },
    { value: IAuditActionType.Delete as number | string, label: AuditActionTypeEnumText[IAuditActionType.Delete] },
    { value: IAuditActionType.Login as number | string, label: AuditActionTypeEnumText[IAuditActionType.Login] },
    { value: IAuditActionType.Verify as number | string, label: AuditActionTypeEnumText[IAuditActionType.Verify] },
    { value: IAuditActionType.OtpEmailSent as number | string, label: AuditActionTypeEnumText[IAuditActionType.OtpEmailSent] },
    { value: IAuditActionType.OtpSmsSent as number | string, label: AuditActionTypeEnumText[IAuditActionType.OtpSmsSent] },
    { value: IAuditActionType.OtpEmailCcSent as number | string, label: AuditActionTypeEnumText[IAuditActionType.OtpEmailCcSent] },
    { value: 'UPDATE_RESET_PASSWORD' as number | string, label: t('PasswordResetType') },
    { value: 'UPDATE_ASSIGN_OTP' as number | string, label: t('OtpAssignedType') },
    { value: 'DELETE_RESET_OTP' as number | string, label: t('OtpResetType') },
  ], [t]);

  const normalize = (raw: any): EventItem => {
    return {
      type: raw.Type ?? raw.type ?? '',
      time: raw.Time ?? raw.time ?? '',
      clientId: raw.ClientId ?? raw.clientId ?? '',
      userId: raw.UserId ?? raw.userId ?? '',
      ipAddress: raw.IpAddress ?? raw.ipAddress ?? '',
      error: raw.Error ?? raw.error ?? null,
      userChanges: raw.UserChanges ?? raw.userChanges ?? null,
      roleChanges: raw.RoleChanges ?? raw.roleChanges ?? null,
      reason: raw.Reason ?? raw.reason ?? null,
      rawChanges: raw.RawChanges ?? raw.rawChanges ?? null,
      actorUserName: raw.ActorUserName ?? raw.actorUserName ?? null,
      actorFullName: raw.ActorFullName ?? raw.actorFullName ?? null
    };
  };

  const fetchEvents = async (overrides?: any) => {
    setError(null);
    setLoading(true);
    try {
      const order = overrides?.order ?? (asc ? 'asc' : 'desc');
      const targetProfileId = overrides?.profileId ?? activeFilters.profileId ?? '';
      const userId = overrides?.userId ?? activeFilters.userId ?? '';
      
      if (!userId) {
        setLoading(false);
        return;
      }

      const selected: (number | string)[] = overrides?.actionTypes ?? activeFilters.actionTypes ?? [];
      const actionTypes = selected.filter((v): v is number => typeof v === 'number');
      const reasons = selected.filter((v): v is string => typeof v === 'string');

      const body = {
        page: overrides?.page ?? page,
        pageSize: overrides?.pageSize ?? pageSize,
        sortBy: overrides?.sortBy ?? sortField,
        order,
        actionTypes,
        reasons,
        dateFrom: toApiDateTime(overrides?.dateFrom ?? activeFilters.dateFrom) ?? null,
        dateTo: toApiDateTime(overrides?.dateTo ?? activeFilters.dateTo, true) ?? null,
        userId: userId,
        userName: null,
        fullName: null,
        userIds: [userId]
      };

      const url = `/api/UserEvents/getprofileevents?targetProfileId=${encodeURIComponent(targetProfileId)}`;
      const result = await post(url, body);
      const respStatus = (result as any)?.response?.status || (result as any)?.status;

      if (respStatus === 200) {
        const payload = (result as any)?.data?.Data ?? (result as any)?.data ?? result?.data;
        const rawItems = Array.isArray(payload?.Items) ? payload.Items : [];
        const items = rawItems.map(normalize);

        setTotal(payload?.TotalCount ?? items.length ?? 0);
        setEvents(items);
        setLoading(false);
      } else if (respStatus === 403) {
        setLoading(false);
        navigate('/error/403');
      } else if (respStatus === 401) {
        setLoading(false);
        navigate('/auth/login');
      } else if (respStatus === 500) {
        setLoading(false);
        navigate('/error/500');
      } else {
        setLoading(false);
        showToast && showToast(t('StatusCode', { code: String(respStatus) }), 'error');
      }
    } catch (e: any) {
      setLoading(false);
      setError(String(e?.message ?? e));
      showToast && showToast(t('ConnectionError', { error: String(e) }), 'error');
    }
  };

  useEffect(() => {
    const navProfile: any = location?.state?.profile ?? null;
    const paramProfileId = (params as any)?.profileId ?? null;

    if (navProfile) {
      const uname = navProfile.Username ?? '';
      const fname = navProfile.Name ?? navProfile.Fullname ?? '';
      const pid = navProfile.ProfileId ?? '';
      const uid = navProfile.UserId ?? '';

      setActiveFilters({ userId: uid, profileId: pid, userName: uname, fullName: fname, actionTypes: [], dateFrom: null, dateTo: null });
      setPrefilledUserName(!!uname);
      setPrefilledFullName(!!fname);
      setPage(1);
    } else if (paramProfileId) {
      setActiveFilters({ userId: '', profileId: paramProfileId, userName: '', fullName: '', actionTypes: [], dateFrom: null, dateTo: null });
      setPrefilledUserName(false);
      setPrefilledFullName(false);
      setPage(1);
    } else {
      setActiveFilters({ userName: '', fullName: '', userId: '', profileId: '', actionTypes: [], dateFrom: null, dateTo: null });
      setPage(1);
    }
    setReady(true);
  }, [location, params]);

  useEffect(() => {
    if (nameRef.current) {
      setTimeout(() => {
        try {
          nameRef.current && nameRef.current.focus();
        } catch (e) {
        }
      }, 0);
    }
  }, [activeFilters.fullName, activeFilters.userName]);

  const handleFilterSearch = async (filters: IFilterProfileEventsModel) => {
    if (loading) {
      return;
    }

    if (filters.dateFrom && filters.dateTo) {
      const from = new Date(filters.dateFrom);
      const to = new Date(filters.dateTo);
      if (to < from) {
        showToast && showToast(t('DateRangeInvalid') ?? 'End date must be the same or after start date', 'warning');
        return;
      }
    }

    setPage(1);
    setActiveFilters(filters);
    await fetchEvents({ page: 1, pageSize, ...filters });
  };

  const handleReset = async (resetForm?: any) => {
    if (loading) {
      return;
    }
    resetForm && resetForm();
    
    const currentUserId = activeFilters.userId;
    const currentProfileId = activeFilters.profileId;
    setActiveFilters({ 
      userName: activeFilters.userName, 
      fullName: activeFilters.fullName, 
      userId: currentUserId, 
      profileId: currentProfileId, 
      actionTypes: [], 
      dateFrom: null, 
      dateTo: null 
    });
    setPage(1);
    if (currentUserId) {
      await fetchEvents({ page: 1, pageSize, userId: currentUserId, profileId: currentProfileId });
    }
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setAsc(!asc);
    } else {
      setSortField(field);
      setAsc(true);
    }
    setPage(1);
  };

  useEffect(() => {
    if (!ready) return;
    (async () => {
      await fetchEvents({ page, pageSize });
    })();
  }, [ready, page, pageSize, sortField, asc]);

  return (
    <div className="container mt-4">
      {/* Build breadcrumb so user can return to FindProfiles and see which profile is open */}
      {(() => {
        const profileLabel = activeFilters.fullName ?? activeFilters.userName ?? null;
        const items = profileLabel ?
          [{ title: t('Home'), href: '/' }, { title: t('UserProfilesList'), href: '/find-profiles' }, { title: `${profileLabel}: ${t('ProfileEvents')}` }] :
          [{ title: t('Home'), href: '/' }, { title: t('UserProfilesList'), href: '/find-profiles' }, { title: t('ProfileEvents') }];
        return <AppBreadcrumb items={items} />;
      })()}

      <div className="d-flex align-items-center mb-3">
        <h3 className="mb-0">{t('ProfileEvents')}</h3>
      </div>

      <Formik initialValues={activeFilters} onSubmit={handleFilterSearch} enableReinitialize>
        {({ resetForm }) => (
          <Form>
            <FiltersCard isActive={true}>
              <div className="mb-3 row g-2 align-items-end">
                <div className="col-md-3 mt-2">
                  <label className="form-label">{t('UserName')}</label>
                  <Field className="form-control" name="userName" disabled={prefilledUserName} />
                </div>

                <div className="col-md-3 mt-2">
                  <label className="form-label">{t('FullName')}</label>
                  <Field className="form-control" name="fullName" disabled={prefilledFullName} />
                </div>

                <div className="col-md-3">
                  <label className="form-label">{t('DateFrom')}</label>
                  <FormikDatePicker name="dateFrom" showTime placeholderText={t('ChooseADate') ?? 'Choose a date'} />
                </div>

                <div className="col-md-3">
                  <label className="form-label">{t('DateTo')}</label>
                  <FormikDatePicker name="dateTo" showTime endOfDayDefault placeholderText={t('ChooseADate') ?? 'Choose a date'} />
                </div>

                <div className="col-md-3 mt-2">
                  <label className="form-label">{t('ActionTypes')}</label>
                  <FormikSelect
                    name="actionTypes"
                    options={actionTypesSelectOptions}
                    isMulti
                    isClearable
                    clearValue={[]}
                    placeholder={`-- ${t('SelectOption')} --`}
                  />
                </div>

                <div className="col-md-9 d-flex justify-content-end align-items-end">
                  <div className="d-flex gap-3">
                    <button type="submit" className="btn gg-btn gg-btn-submit">{t('Search')}</button>
                    <button type="button" className="btn btn-secondary text-white" onClick={() => handleReset(resetForm)}>{t('Reset')}</button>
                  </div>
                </div>
              </div>
            </FiltersCard>
          </Form>
        )}
      </Formik>

      <div className="mb-3">
        <BackButton goTo="/find-profiles" />
      </div>

      {(activeFilters.fullName || activeFilters.userName) && (
        <div className="mb-3">
          <div className="card">
            <div className="card-body py-2 d-flex align-items-center">
              <span className="me-2 text-muted">{t('EventsOfProfileLabel')}</span>
              <span
                ref={nameRef}
                tabIndex={-1}
                className="fw-bold text-danger"
                aria-live="polite"
              >
                {activeFilters.fullName ?? activeFilters.userName}
              </span>
            </div>
          </div>
        </div>
      )}

      {error && <div className="alert alert-danger">{error}</div>}

      {loading && <Loader />}

      <div className="table-responsive">
        <table className="table table-striped table-sm">
          <colgroup>
            <col style={{ width: '100px' }} />
            <col style={{ width: '160px' }} />
            <col style={{ width: '130px' }} />
            <col style={{ width: '150px' }} />
            <col />
          </colgroup>
          <thead>
            <tr>
              <th>{t('Type')}</th>
              <th style={{ cursor: 'pointer' }} onClick={() => handleSort('timestamp')}>{t('Timestamp')} <SortDirection isActive={sortField === 'timestamp'} order={asc ? 'asc' : 'desc'} /></th>
              <th>{t('IpAddress')}</th>
              <th>{t('ChangedBy')}</th>
              <th>{t('Changes')}</th>
            </tr>
          </thead>
          <tbody className="table-group-divider">
            {events.length === 0 && !loading && (
              <tr>
                <td colSpan={5} className="text-center">{t('NoEventsDataFound') ?? 'No events'}</td>
              </tr>
            )}
            {events.map((log, i) => {
              const special = log.reason ? specialReasonLabels[log.reason] : undefined;
              const displayType = special ? t(special.typeKey) : log.type;
              const displayMessage = special
                ? t(special.messageKey)
                : (log.reason && log.reason.toLowerCase() !== log.type.toLowerCase() ? log.reason : null);
              return (
              <tr key={`${log.userId}-${log.time}-${i}`} className={log.error ? 'table-logs__item--error' : ''}>
                <td>{displayType}</td>
                <td>{formatDateTime(parseUtc(log.time), false)}</td>
                <td>{log.ipAddress}</td>
                <td>
                   {log.type !== 'Login' && log.type !== 'Verify'
                      ? (log.actorFullName || log.actorUserName || null)
                      : null}
                </td>
                <td>
                  {}
                  {displayMessage && (
                    <div className="mb-2">{displayMessage}</div>
                  )}

                  {}
                  {log.userChanges && Object.keys(log.userChanges).length > 0 && (
                    <div className="mb-2 profile-events">
                      <strong>{t('UserChanges')}:</strong>
                      <ul className="mb-0 changes-list">
                        {Object.entries(log.userChanges).map(([k, v]) => (
                          <li key={k} className="mb-1">
                            <div className="changes-row">
                              <div className="changes-prop">{k}:</div>
                              <div
                                className="change-badge old"
                                aria-label={`${t('OldValue') ?? 'Old'}: ${v.oldValue ?? '—'}`}
                                title={v.oldValue ?? ''}
                              >
                                <span className="change-label">{t('OldValue') ?? 'Old'}:</span>
                                <span className="change-value">{v.oldValue ?? '—'}</span>
                              </div>
                              <div
                                className="change-badge new"
                                aria-label={`${t('NewValue') ?? 'New'}: ${v.newValue ?? '—'}`}
                                title={v.newValue ?? ''}
                              >
                                <span className="change-label">{t('NewValue') ?? 'New'}:</span>
                                <span className="change-value">{v.newValue ?? '—'}</span>
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {}
                  {log.roleChanges && (log.roleChanges.added?.length || log.roleChanges.removed?.length) ? (
                    <div className="mb-2">
                      <strong>{t('RoleChanges') ?? 'Role Changes'}:</strong>
                      {log.roleChanges.added?.length ? (
                        <div className="small mt-1">
                          <span className="badge bg-success me-1">{t('Added') ?? 'Added'}</span>
                          {log.roleChanges.added.join(', ')}
                        </div>
                      ) : null}
                      {log.roleChanges.removed?.length ? (
                        <div className="small mt-1">
                          <span className="badge bg-danger me-1">{t('Removed') ?? 'Removed'}</span>
                          {log.roleChanges.removed.join(', ')}
                        </div>
                      ) : null}
                    </div>
                  ) : null}

                  {}
                  {log.rawChanges && (
                    <div className="mb-2">
                      <strong>{t('Changes') ?? 'Changes'}:</strong>
                      <pre className="bg-light p-2 border rounded small mt-1 mb-0" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {(() => { try { return JSON.stringify(JSON.parse(log.rawChanges), null, 2); } catch { return log.rawChanges; } })()}
                      </pre>
                    </div>
                  )}

                  {}
                  {log.error && (
                    <div className="text-danger small mt-1">
                      <strong>{t('ErrorMessage') ?? 'Error'}:</strong> {log.error}
                    </div>
                  )}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Pagination
        currentPage={page}
        onPageChange={(p) => setPage(p)}
        totalItems={total}
        pageSize={pageSize}
        onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
      />
    </div>
  );
}
